/* eslint-disable arrow-body-style */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Avatar,
  CircularProgress,
  Typography,
  Chip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { format, parseISO } from 'date-fns';
import Scrollbar from '../scrollbar';
import { TABLE_HEAD } from '../../utils/incomeUtils';

const getStatusColor = (status) => {
  switch (status) {
    case 'Received':
      return 'success';
    case 'Pending':
      return 'warning';
    case 'Cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const IncomeTable = ({
  incomes,
  isLoading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onView,
  onEdit,
  onDelete,
}) => {
  return (
    <Grid item xs={12}>
      <Card>
        <Scrollbar>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((headCell) => (
                    <TableCell key={headCell.id} align={headCell.alignRight ? 'right' : 'left'}>
                      {headCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (incomes || []).length === 0 ? (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                      <Paper sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2">No incomes found.</Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {incomes.map((income) => (
                    <TableRow hover key={income.id}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar>
                            <TrendingUpIcon />
                          </Avatar>
                          <Typography variant="subtitle2">{income.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{income.category}</TableCell>
                      <TableCell>{income.amount}</TableCell>
                      <TableCell>{income.currency}</TableCell>
                      <TableCell>{income.date ? format(parseISO(income.date), 'MMM dd, yyyy') : '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={income.status}
                          color={getStatusColor(income.status)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>
                          {income.remarks || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <IconButton size="small" onClick={() => onView(income)} sx={{ color: 'primary.main' }}>
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => onEdit(income)} sx={{ color: 'primary.main' }}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => onDelete(income)} sx={{ color: 'error.main' }}>
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Card>
    </Grid>
  );
};

IncomeTable.propTypes = {
  incomes: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default IncomeTable;
